import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { authState } from "src/store/auth";
import {
  useGetCities,
  useGetCountries,
} from "src/api/generic";
import { useConnectZatca } from "src/api/integrations/zatca";

interface IProps {
  open: boolean;
  handleClose: () => void;
  refetch: () => void;
}

const schema = yup.object({
  zatca_otp: yup.string().required("required"),
  tax_registration_number: yup.string().required("required"),
  commercial_registration_number: yup.string().required("required"),
  street_address: yup.string().required("required"),
  building_number: yup.string().required("required"),
  district: yup.string().required("required"),
  city_id: yup.string().required("required"),
  country_id: yup.string().required("required"),
  postal_code: yup.string().required("required"),
  secondary_number: yup.string(),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const CompanyInfoPopup = (props: IProps) => {
  const { open, handleClose } = props;
  const { t } = useTranslation("profile");
  const { t: integrationT } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [auth] = useRecoilState(authState);
  const company = auth?.user?.company;

  // APIS
  const { mutate, status, error, isPending } = useConnectZatca();
  const { data: countriesData } = useGetCountries();
  const { data: citiesData } = useGetCities();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    mutate({
      id: company.id,
      tax_registration_number: data.tax_registration_number,
      commercial_registration_number: data.commercial_registration_number,
      force: false,
      zatca_otp: data.zatca_otp,

      address: {
        street_address: data.street_address,
        building_number: data.building_number,
        district: data.district,
        city_id: data.city_id,
        country_id: data.country_id,
        postal_code: data.postal_code,
        secondary_number: data.secondary_number ?? "",
      },
    });
  };

  //Update success and error handling
  useEffect(() => {
    if (status === "success") {
      window.location.reload();
      handleClose();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    reset({
      tax_registration_number: company.tax_registration_number,
      commercial_registration_number: company.commercial_registration_number,
      street_address: company.address?.street_address,
      building_number: company.address?.building_number,
      district: company.address?.district,
      city_id: company.address?.city?.id,
      country_id: company.address?.country?.id,
      postal_code: company.address?.postal_code,
      secondary_number: company.address?.secondary_number ?? "",
    });
  }, [company]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {t("companyDetails")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="zatca_otp"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={integrationT("zatca.zatcaOtp")}
                  error={!!errors.zatca_otp}
                  helperText={errors.zatca_otp?.message}
                />
              )}
            />

            <Controller
              name="tax_registration_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("taxRegistrationNumber")}
                  error={!!errors.tax_registration_number}
                  helperText={errors.tax_registration_number?.message}
                />
              )}
            />

            <Controller
              name="commercial_registration_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("commercialRegistrationNumber")}
                  error={!!errors.commercial_registration_number}
                  helperText={errors.commercial_registration_number?.message}
                />
              )}
            />

            <Controller
              name="secondary_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("secondaryNumber")}
                  error={!!errors.secondary_number}
                  helperText={errors.secondary_number?.message}
                />
              )}
            />

            <Controller
              name="street_address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("streetAddress")}
                  error={!!errors.street_address}
                  helperText={errors.street_address?.message}
                />
              )}
            />

            <Controller
              name="building_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("buildingNumber")}
                  error={!!errors.building_number}
                  helperText={errors.building_number?.message}
                />
              )}
            />

            <Controller
              name="district"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("district")}
                  error={!!errors.district}
                  helperText={errors.district?.message}
                />
              )}
            />

            <Controller
              name="country_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  size="small"
                  options={countriesData?.data?.countries ?? []}
                  getOptionLabel={(option) => option?.name ?? ""}
                  value={
                    countriesData?.data?.countries?.find(
                      (country) => `${country.id}` === `${field.value}`,
                    ) || null
                  }
                  onChange={(_, value) =>
                    field.onChange(value ? `${value.id}` : "")
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label={generalT("branchCountry")}
                      error={!!errors.country_id}
                      helperText={errors.country_id?.message}
                    />
                  )}
                />
              )}
            />

            <FormControl fullWidth size="small" error={!!errors.city_id}>
              <InputLabel>{generalT("branchLocation")}</InputLabel>

              <Controller
                name="city_id"
                control={control}
                render={({ field }) => (
                  <Select {...field} label={generalT("branchLocation")}>
                    {citiesData?.data?.cities?.map(({ id, name }) => (
                      <MenuItem key={id} value={`${id}`}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />

              <FormHelperText>{errors.city_id?.message}</FormHelperText>
            </FormControl>

            <Controller
              name="postal_code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("postalCode")}
                  error={!!errors.postal_code}
                  helperText={errors.postal_code?.message}
                />
              )}
            />
          </Stack>

          <Stack mt={3} spacing={2} direction="row">
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>

            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
            >
              {generalT("submit")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default CompanyInfoPopup;
